/**
 * Choose what to print from this deal, and print it.
 *
 * Export Book and Package were two buttons opening this one dialog behind a
 * variant flag, against one server pipeline. They printed overlapping menus
 * over 70% shared plumbing, and the reader's only real question — which one
 * am I supposed to send? — had no good answer. They are now one document:
 * the **Deal Dossier**, their union on the institutional furniture, with
 * stage-banding and photo embedding surviving as options inside it.
 *
 * Every printed section has exactly ONE id, so a section can never be selected
 * twice under two names. Two package sections used to be strict subsets of a
 * book section rendering the same records — "Deal Notes" inside "Notes &
 * Activity", "Triggers & Ticklers" inside "Conditions & Obligations" — and
 * ticking both printed one section twice. The supersets won; the retired ids
 * still resolve server-side so saved selections keep working.
 *
 * Three rules it exists to keep:
 *
 *   1. **Availability is stated, never hidden.** The server says, per deal,
 *      which sections actually hold data. An empty one stays listed and
 *      disabled — a reviewer asking "where is the committee memo" is owed
 *      "this deal has none", not a menu that quietly omits it.
 *   2. **Empty means everything, because that is the server's contract.**
 *      Sending no sections prints them all, so the dialog never sends an empty
 *      list by accident: exporting nothing is blocked at the button.
 *   3. **Photos are opt-in.** Embedding every image on a deal into a credit
 *      package is not a default anybody wants — a deal's documents include
 *      identity scans and payment instructions that arrive as images, and a
 *      filer can file one as marketing. Nothing here is ticked for the reader. A
 *      picture is printed only if it is chosen here, on either form (it then
 *      leads the cover, and fills the Photographs section of the full record), or
 *      if it is a standalone image filed under the category "photos", or if it
 *      is a picture inside the deal's offering memorandum or marketing PDF. An
 *      image filed as marketing or as an offering memorandum is listed here and
 *      printed only when chosen.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileSpreadsheet, FileText, Image as ImageIcon, Loader2, X } from 'lucide-react'

import {
  dealPackageService,
  downloadDealPackage,
  type DealDocumentKind,
  type DealPackagePhoto,
  type DealPackageSection,
  type DealPdfForm,
} from '@/services/dealPackage'

export type DealPrintFormat = DealDocumentKind

export interface DealPackageDialogProps {
  loanId: string
  onClose: () => void
  /** Which format the dialog opens on. The reader can switch it. */
  initialFormat?: DealPrintFormat
}

/** What each dossier PDF form tells the reader it is producing (owner
 * decision #2, 25 Sep 2026). The full document is the selectable record; the
 * summary is a fixed 2-4 page form built from the same field-lineage data, so
 * its section/stage/photo pickers do not apply. Both are set in Helios Paper
 * Gold with vector charts drawn from the recorded figures. */
const PDF_FORM_HINT: Record<DealPdfForm, string> = {
  full:
    'The complete institutional record of this deal — a cover with the photograph ' +
    'you choose (or the place, drawn from its coordinates) and headline figures, ' +
    'contents, an executive summary with credit gauges and ' +
    'the capital stack, and every section the deal holds, each opened with the ' +
    'charts its record supports.',
  summary:
    'A 2-4 page Executive Summary: headline figures, credit gauges against their ' +
    'limits and the capital stack where the record has them, what deserves a ' +
    'second look and the recorded fields — every value marked Stated, Derived, ' +
    'Suggested or Confirmed, same as the full record.',
}

/** What the Photos list does, said as the server does it (`backend/app/reporting/dossier_media.py`): a picture
 * is printed because it is chosen, never because it is an image. The two cases it is printed unasked are a
 * standalone image filed as a photo and a picture inside the deal's offering memorandum or marketing PDF; an
 * image filed as marketing is listed and prints only when chosen. */
const FULL_PHOTO_HINT =
  'A photo you choose leads the cover and fills the Photographs section. An image you leave unticked ' +
  'is not printed unless it is filed under the category “photos”, and pictures inside the deal’s ' +
  'offering memorandum or marketing PDF can also appear on the cover. An image filed as marketing is ' +
  'listed here and prints only if you choose it.'

const SUMMARY_PHOTO_HINT =
  'A photo you choose leads the cover of the summary. With none chosen the cover shows a picture from ' +
  'the deal’s offering memorandum or marketing PDF, or an image filed as a photo, if there is one; ' +
  'otherwise the place, drawn from its coordinates, or where the deal stands.'

/** What the per-deal Excel workbook holds, for its button's tooltip. */
const WORKBOOK_HINT =
  'A dashboard of native Excel charts over the deal’s figures, the Deal Summary ' +
  'with the basis of each, and every catalog field with its key and state — Excel'

/** What each output adds for a portfolio parent. The server offers the Portfolio section for a parent and for no
 * other deal, so these are said only where the deal's own options list it. */
const PORTFOLIO_HINT = {
  full:
    'This deal is a portfolio: its Portfolio section lists the properties and rolls up the ' +
    'underwriting models they carry.',
  summary:
    'This deal is a portfolio: the summary also carries its properties and the roll-up of their models.',
  workbook:
    'This deal is a portfolio: the workbook also has a Portfolio sheet of its properties and the ' +
    'roll-up of their models.',
} as const

function SectionRow({
  section,
  checked,
  onToggle,
}: {
  section: DealPackageSection
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label
      className={`flex items-start gap-2 rounded px-1.5 py-1 text-[11px] ${
        section.available ? 'cursor-pointer hover:bg-white/[0.03]' : 'cursor-not-allowed'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={!section.available}
        onChange={onToggle}
        aria-label={section.label}
        className="mt-0.5 h-3 w-3 shrink-0 accent-amber-300 disabled:opacity-30"
      />
      <span className="min-w-0">
        <span className={section.available ? 'text-stone-200' : 'text-stone-600'}>
          {section.label}
        </span>
        {!section.available && (
          <span className="block text-[9px] leading-tight text-stone-600">
            Nothing recorded on this deal
          </span>
        )}
      </span>
    </label>
  )
}

/** The deal's image documents, each a checkbox. Nothing is ticked for the reader: a
 * picture is printed because it was chosen, never because it is an image. */
function PhotoPicker({
  photos,
  chosen,
  onToggle,
  hint,
}: {
  photos: DealPackagePhoto[]
  chosen: Set<string>
  onToggle: (id: string) => void
  hint: string
}) {
  return (
    <section className="min-w-0">
      <h3 className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-500">
        Photos
        {photos.length > 0 && (
          <span className="ml-1 text-stone-600">
            {chosen.size} of {photos.length}
          </span>
        )}
      </h3>
      {photos.length === 0 ? (
        <p className="text-[10px] leading-tight text-stone-600">
          No image documents on this deal. Photos are uploaded through Documents.
        </p>
      ) : (
        <>
          <p className="mb-1 text-[10px] leading-tight text-stone-600">{hint}</p>
          <div className="space-y-0.5">
            {photos.map((photo) => (
              <label
                key={photo.id}
                className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-white/[0.03]"
              >
                <input
                  type="checkbox"
                  checked={chosen.has(photo.id)}
                  onChange={() => onToggle(photo.id)}
                  aria-label={photo.filename}
                  className="mt-0.5 h-3 w-3 shrink-0 accent-amber-300"
                />
                <ImageIcon size={11} className="mt-0.5 shrink-0 text-stone-600" />
                <span className="min-w-0 break-all text-stone-200">
                  {photo.filename}
                  {photo.category && (
                    <span className="block text-[9px] text-stone-600">{photo.category}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

export default function DealPackageDialog({
  loanId,
  onClose,
  initialFormat = 'dossier',
}: DealPackageDialogProps) {
  const [format] = useState<DealPrintFormat>(initialFormat)
  // The legacy book printed neither stage bands nor photos, so it collapsed
  // those panels. The dossier carries both, so it uses the full layout.
  const isBook = format === 'book'
  // The short form only exists for the dossier — 'package'/'book' keep their
  // one shape. Section/stage/photo choices are disabled once the reader picks
  // it: the summary is a fixed set of fields, not a selectable print.
  const [pdfForm, setPdfForm] = useState<DealPdfForm>('full')
  const isSummary = format === 'dossier' && pdfForm === 'summary'
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['deal-package-options', loanId, format],
    queryFn: () => dealPackageService.getOptions(loanId, format),
    retry: false,
  })

  const [sections, setSections] = useState<Set<string> | null>(null)
  // Every section id either format has offered so far, so a switch adds
  // only genuinely new sections rather than re-ticking ones the reader cleared.
  const seenSectionIds = useRef(new Set<string>())
  const reconciledSignature = useRef<string | null>(null)
  const [stages, setStages] = useState<Set<string>>(new Set())
  const [photos, setPhotos] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // Only a format switch needs reconciling: a section this format cannot print
  // is dropped, and one it can print that the reader has not seen is selected.
  // Because every printed section has one id, a choice already made about a
  // section both formats print (Overview, Underwriting, Documents, Conditions,
  // Notes & Activity) carries across untouched instead of being reset.
  //
  // An untouched selection is left null so the new format's default applies.
  useEffect(() => {
    if (!data) return
    // Reconcile once per distinct catalogue. The effect can run again for the
    // same data, and a second pass would see every id as already-seen and stop
    // selecting the sections this format just introduced.
    const signature = data.sections.map((section) => section.id).join('|')
    const previous = reconciledSignature.current
    if (previous === signature) return
    reconciledSignature.current = signature

    const newlyOffered = data.sections
      .filter((section) => section.available && !seenSectionIds.current.has(section.id))
      .map((section) => section.id)
    const inThisFormat = new Set(data.sections.map((section) => section.id))
    for (const section of data.sections) seenSectionIds.current.add(section.id)

    if (previous === null) return
    setSections((current) =>
      current === null
        ? null
        : new Set([...[...current].filter((id) => inThisFormat.has(id)), ...newlyOffered]),
    )
  }, [data])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  // The default is DERIVED, not stored. Selecting a section with nothing in it
  // prints a heading over "None recorded", which reads as a gap in the deal
  // rather than a choice the operator made — so the default is every section
  // this deal can fill.
  //
  // It used to be written into state by an effect, which left a window: the
  // options resolve, the dialog renders its checkboxes, and a click landing
  // before that effect flushed started from an EMPTY set and silently dropped
  // every default. Deriving it closes the window — `sections` stays null until
  // the reader actually changes something, and null means "the default".
  const defaultSelection = useMemo(
    () =>
      new Set(
        (data?.sections ?? [])
          .filter((section) => section.available)
          .map((section) => section.id),
      ),
    [data],
  )
  const chosen = sections ?? defaultSelection
  const availableCount = data?.sections.filter((s) => s.available).length ?? 0
  // Offered for a portfolio parent only, whether or not it holds a property yet.
  const isPortfolio = data?.sections.some((s) => s.id === 'portfolio') ?? false

  const toggle = (set: Set<string>, id: string, apply: (next: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    apply(next)
  }

  const [workbookBusy, setWorkbookBusy] = useState(false)
  const runWorkbook = async () => {
    setWorkbookBusy(true)
    setFailure(null)
    try {
      const out = await dealPackageService.generateWorkbook(loanId)
      downloadDealPackage(out.blob, out.filename)
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'The workbook could not be generated.')
    } finally {
      setWorkbookBusy(false)
    }
  }

  // The summary is a fixed set of fields — it has no sections to choose, so
  // it never blocks on an empty selection the way the full dossier does.
  const canGenerate = !!data && (isSummary || chosen.size > 0)

  const run = async () => {
    if (!canGenerate) return
    setBusy(true)
    setFailure(null)
    try {
      const out = await dealPackageService.generate({
        loanId,
        documentKind: format,
        pdfForm: format === 'dossier' ? pdfForm : undefined,
        // Every available section selected still sends the explicit list rather
        // than an empty one: empty means "all sections" server-side, including
        // the ones this deal cannot fill. Ignored server-side for the summary.
        sections: [...chosen],
        stages: [...stages],
        photoDocumentIds: [...photos],
      })
      downloadDealPackage(out.blob, out.filename)
      onClose()
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'The package could not be generated.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Print this deal"
      // The modal is portalled by DealDossier; containment remains defensive
      // for standalone use and for tests that mount it under another overlay.
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[86vh] w-[860px] max-w-[94vw] flex-col overflow-hidden rounded-lg border border-amber-400/20 bg-black">
        <header className="flex shrink-0 items-center justify-between border-b border-amber-400/15 px-4 py-2.5">
          <div className="min-w-0">
            <h2 className="flex items-center gap-1.5 text-[13px] font-semibold text-stone-100">
              <FileText size={13} className="text-amber-300" />
              Deal Dossier
            </h2>
            <p className="truncate text-[10px] text-stone-500">
              {data ? `${data.propertyName} · currently ${data.currentStage}` : 'Loading options…'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {format === 'dossier' && (
              <div
                role="radiogroup"
                aria-label="PDF form"
                className="flex items-center rounded border border-stone-700 p-0.5 text-[10px]"
              >
                {(['full', 'summary'] as const).map((form) => (
                  <button
                    key={form}
                    type="button"
                    role="radio"
                    aria-checked={pdfForm === form}
                    onClick={() => setPdfForm(form)}
                    className={`rounded px-2 py-0.5 ${
                      pdfForm === form
                        ? 'bg-amber-300/20 text-amber-100'
                        : 'text-stone-500 hover:text-stone-300'
                    }`}
                  >
                    {form === 'full' ? 'Complete Deal Lifecycle' : 'Executive Summary'}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-stone-500 hover:text-stone-200"
            >
              <X size={14} />
            </button>
          </div>
        </header>
        <p className="shrink-0 border-b border-amber-400/10 px-4 py-1.5 text-[10px] text-stone-500">
          {PDF_FORM_HINT[isSummary ? 'summary' : 'full']}
          {isPortfolio ? ` ${PORTFOLIO_HINT[isSummary ? 'summary' : 'full']}` : ''}
        </p>

        {isError && (
          <p className="px-4 py-6 text-[11px] text-red-300">
            The package options could not be read
            {error instanceof Error && error.message ? ` — ${error.message}` : '.'}
          </p>
        )}

        {isLoading && (
          <p className="px-4 py-6 text-[11px] text-stone-500" aria-busy="true">
            Reading what this deal holds…
          </p>
        )}

        {data && isSummary && (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <p className="text-[11px] leading-relaxed text-stone-400">
              The Executive Summary prints a fixed set of fields — headline
              figures, credit gauges, the capital stack, deal identity,
              requested terms, sponsor/borrower, key underwriting metrics,
              collateral summary, key dates and the current pipeline stage —
              so it has no sections or stages to choose. Switch to{' '}
              <span className="text-stone-200">Complete Deal Lifecycle</span>{' '}
              above for the full, selectable record.
            </p>
            {data.photos.length > 0 && (
              <div className="mt-3">
                <PhotoPicker
                  photos={data.photos}
                  chosen={photos}
                  onToggle={(id) => toggle(photos, id, setPhotos)}
                  hint={SUMMARY_PHOTO_HINT}
                />
              </div>
            )}
          </div>
        )}

        {data && !isSummary && (
          <div className="grid min-h-0 flex-1 grid-cols-3 gap-4 overflow-y-auto px-4 py-3">
            <section className={`min-w-0 ${isBook ? 'col-span-3' : ''}`}>
              <h3 className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-500">
                Sections
                <span className="ml-1 text-stone-600">
                  {chosen.size} of {availableCount}
                </span>
              </h3>
              <div className={isBook ? 'grid grid-cols-1 gap-x-4 sm:grid-cols-2' : 'space-y-0.5'}>
                {data.sections.map((s) => (
                  <SectionRow
                    key={s.id}
                    section={s}
                    checked={chosen.has(s.id)}
                    onToggle={() => toggle(chosen, s.id, (next) => setSections(next))}
                  />
                ))}
              </div>
            </section>

            {!isBook && <section className="min-w-0">
              <h3 className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-500">
                Stages
              </h3>
              <p className="mb-1 text-[9px] leading-tight text-stone-600">
                Limits the stage-banded sections. None selected prints every stage.
              </p>
              <div className="space-y-0.5">
                {data.stages.map((stage) => (
                  <label
                    key={stage}
                    className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] text-stone-200 hover:bg-white/[0.03]"
                  >
                    <input
                      type="checkbox"
                      checked={stages.has(stage)}
                      onChange={() => toggle(stages, stage, setStages)}
                      aria-label={stage}
                      className="h-3 w-3 shrink-0 accent-amber-300"
                    />
                    {stage}
                  </label>
                ))}
              </div>
            </section>}

            {!isBook && (
              <PhotoPicker
                photos={data.photos}
                chosen={photos}
                onToggle={(id) => toggle(photos, id, setPhotos)}
                hint={FULL_PHOTO_HINT}
              />
            )}
          </div>
        )}

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-amber-400/15 px-4 py-2.5">
          <p className="min-w-0 text-[10px] text-stone-500">
            {failure ? (
              <span className="text-red-300">{failure}</span>
            ) : chosen.size === 0 && data && !isSummary ? (
              'Choose at least one section.'
            ) : (
              isBook
                ? 'Exports a paginated institutional deal book.'
                : format === 'dossier'
                  ? isSummary
                    ? 'Exports a 2-4 page Executive Summary as a Paper Gold PDF.'
                    : 'Exports the full deal dossier as a Paper Gold PDF.'
                  : 'Prints as a Paper Gold PDF.'
            )}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-stone-700 px-2.5 py-1 text-[11px] text-stone-300 hover:bg-stone-900"
            >
              Cancel
            </button>
            {/* The per-deal Excel workbook had no control on the deal at all —
                only a Reporting menu entry (REG-12). */}
            <button
              type="button"
              onClick={runWorkbook}
              disabled={busy || workbookBusy}
              title={isPortfolio ? `${WORKBOOK_HINT}. ${PORTFOLIO_HINT.workbook}` : WORKBOOK_HINT}
              className="inline-flex items-center gap-1.5 rounded border border-emerald-400/35 px-2.5 py-1 text-[11px] text-emerald-100 disabled:opacity-40"
            >
              {workbookBusy ? <Loader2 size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />}
              {workbookBusy ? 'Building…' : 'Excel workbook'}
            </button>
            <button
              type="button"
              onClick={run}
              disabled={!canGenerate || busy}
              className="inline-flex items-center gap-1.5 rounded border border-amber-300/45 bg-amber-300/[0.12] px-2.5 py-1 text-[11px] text-amber-100 disabled:opacity-40"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
              {busy
                ? 'Building…'
                : isBook
                  ? 'Export book'
                  : format === 'dossier'
                    ? isSummary
                      ? 'Generate summary'
                      : 'Generate dossier'
                    : 'Generate package'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
